'use client';

import { MessageSquare, X } from 'lucide-react';
import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cn } from '@/app/_lib/utils';

export type FabButtonVariant = 'default' | 'secondary' | 'outline';
export type FabButtonShape = 'circle' | 'square';

export interface FabButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  isOpen: boolean;
  label: string;
  variant?: FabButtonVariant;
  shape?: FabButtonShape;
  /** When false, button renders inline (no fixed positioning). Defaults to true. */
  floating?: boolean;
}

const getVariantClasses = (variant: FabButtonVariant): string => {
  switch (variant) {
    case 'secondary':
      return [
        'bg-secondary text-secondary-foreground hover:bg-secondary',
        'shadow-fab-neutral-mobile lg:shadow-fab-neutral-desktop',
        'focus-visible:outline-foreground',
      ].join(' ');
    case 'outline':
      return [
        'bg-button-outline-background text-foreground border-2 border-input hover:bg-accent',
        'shadow-fab-neutral-mobile lg:shadow-fab-neutral-desktop',
        'focus-visible:outline-foreground',
      ].join(' ');
    default:
      return [
        'bg-primary text-primary-foreground hover:bg-primary',
        'shadow-fab-mobile lg:shadow-fab-desktop',
        'focus-visible:outline-primary',
      ].join(' ');
  }
};

const getShapeClass = (shape: FabButtonShape): string =>
  shape === 'square' ? 'rounded-fab-bar-button' : 'rounded-full';

export const FabButton = forwardRef<HTMLButtonElement, FabButtonProps>(
  (
    {
      isOpen,
      label,
      variant = 'default',
      shape = 'circle',
      floating = true,
      disabled,
      className,
      ...props
    },
    ref
  ) => {
    return (
      <button
        ref={ref}
        type="button"
        aria-label={label}
        aria-expanded={isOpen}
        disabled={disabled}
        className={[
          floating
            ? 'fixed right-6 bottom-6 z-fab flex size-16 items-center justify-center'
            : 'flex size-12 items-center justify-center shrink-0',
          getShapeClass(shape),
          getVariantClasses(variant),
          'transition duration-200',
          // Hover effects only on real-hover devices — avoids the sticky-hover
          // zoom that lingers after a tap on touch screens.
          '[@media(hover:hover)]:data-[state=closed]:hover:scale-fab-hover',
          'data-[state=open]:scale-fab-hover',
          '[@media(hover:hover)]:hover:brightness-110',
          'active:opacity-60',
          'focus-visible:outline-2 focus-visible:outline-offset-2',
          disabled
            ? 'cursor-not-allowed opacity-50'
            : 'cursor-pointer',
          className,
        ]
          .filter(Boolean)
          .join(' ')}
        {...props}
      >
        <MessageSquare
          className={cn(
            'absolute size-6 transition-[transform,opacity] duration-300',
            isOpen ? 'rotate-90 opacity-0' : 'rotate-0 opacity-100'
          )}
          aria-hidden
        />
        <X
          className={cn(
            'absolute size-6 transition-[transform,opacity] duration-300',
            isOpen ? 'rotate-0 opacity-100' : '-rotate-90 opacity-0'
          )}
          aria-hidden
        />
      </button>
    );
  }
);
FabButton.displayName = 'FabButton';

export default FabButton;
